import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Avatar,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Tag,
  Divider,
  Space,
  ConfigProvider,
  Spin,
  message
} from 'antd';
import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
  BankOutlined,
  TeamOutlined,
  DollarOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Link } = Typography;

const IncubatorDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const brandColor = '#470172';
  const [loading, setLoading] = useState(true);
  const [incubatorData, setIncubatorData] = useState(null);
  const [logoError, setLogoError] = useState(false);

  const API_URL = process.env.REACT_APP_API_BASE_URL || '';

  // Default fallback images
  const defaultLogo = 'https://ui-avatars.com/api/?name=Incubator&background=470172&color=fff&size=128&bold=true';
  const defaultBanner = 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2070&auto=format&fit=crop';

  // Helper function to get image URL from server
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const normalizedPath = imagePath.replace(/\\/g, '/').replace(/^\/+/, '');
    if (normalizedPath.startsWith('uploads/')) {
      return `${API_URL}/${normalizedPath}`;
    }
    return `${API_URL}/uploads/${normalizedPath}`;
  };

  useEffect(() => {
    fetchIncubatorDetails();
  }, [id]);

  const fetchIncubatorDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/incubator/${id}`);
      setIncubatorData(response.data);
    } catch (error) {
      message.error('Failed to load incubator details');
      console.error('Error fetching incubator:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'approved' || status === 'active') return 'success';
    if (status === 'pending') return 'warning';
    return 'default';
  };

  const logoUrl = incubatorData?.logo ? getImageUrl(incubatorData.logo) : defaultLogo;
  const bannerUrl = defaultBanner;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: brandColor }} spin />} />
      </div>
    );
  }

  if (!incubatorData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f0f2f5' }}>
        <Text type="secondary">No incubator data available.</Text>
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brandColor,
          fontFamily: "'Rubik', sans-serif",
          borderRadius: 6,
          fontSize: 16,
        },
      }}
    >
      <div style={{
        width: '100%',
        minHeight: '100%',
        padding: "20px 10px",
        backgroundColor: '#f0f2f5',
        position: 'relative',
        overflowX: 'hidden',
        fontFamily: "'Rubik', sans-serif"
      }}>
        {/* Back Button */}
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto 20px auto',
          paddingLeft: '10px'
        }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{
              fontSize: '16px',
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 400,
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: brandColor,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = brandColor;
              e.currentTarget.style.color = brandColor;
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(71, 1, 114, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d9d9d9';
              e.currentTarget.style.color = brandColor;
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Back
          </Button>
        </div>

        {/* Banner Section */}
        <div style={{
          height: '290px',
          background: `url(${bannerUrl}) center center / cover no-repeat`,
          position: 'relative',
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(to bottom, rgba(71, 1, 114, 0.3), rgba(0,0,0,0.6))'
          }} />
        </div>

        {/* Content Container */}
        <div style={{
          padding: '0 10px 24px',
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* Header Card */}
          <Card
            bordered={false}
            style={{
              marginTop: -60,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '100%'
            }}
            bodyStyle={{ padding: '24px 32px' }}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} sm={6} md={4} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  marginTop: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {logoError || !incubatorData?.logo ? (
                    <Avatar
                      size={130}
                      style={{
                        backgroundColor: brandColor,
                        fontSize: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}
                      icon={<UserOutlined />}
                    >
                      {incubatorData?.incubatorName?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  ) : (
                    <img
                      src={logoUrl}
                      alt="Incubator Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        padding: '8px'
                      }}
                      onError={() => {
                        console.error('Logo failed to load:', logoUrl);
                        setLogoError(true);
                      }}
                      onLoad={() => console.log('Logo loaded successfully')}
                    />
                  )}
                </div>
              </Col>

              <Col xs={24} sm={18} md={20}>
                <Space direction="vertical" size={8}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: brandColor,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500,
                      fontSize: '32px'
                    }}
                  >
                    {incubatorData.incubatorName || 'Incubator Name'}
                  </Title>
                  <Space wrap>
                    <Tag
                      color="purple"
                      style={{
                        margin: 0,
                        fontSize: '16px',
                        padding: '4px 12px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      {incubatorData.focusAreas || incubatorData.industry || 'Not Specified'}
                    </Tag>
                    {incubatorData.isVerified && (
                      <Tag
                        icon={<SafetyCertificateOutlined />}
                        color="success"
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        Verified
                      </Tag>
                    )}
                    {incubatorData.status && (
                      <Tag
                        color={getStatusColor(incubatorData.status)}
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        {incubatorData.status.charAt(0).toUpperCase() + incubatorData.status.slice(1)}
                      </Tag>
                    )}
                  </Space>
                  {incubatorData.website && (
                    <Link
                      href={incubatorData.website}
                      target="_blank"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: '16px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400,
                        color: "#333"
                      }}
                    >
                      <GlobalOutlined /> {incubatorData.website}
                    </Link>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Details Grid */}
          <Row gutter={[24, 24]} style={{ marginTop: 24, width: '100%', maxWidth: '100%' }}>
            
            {/* Left Column: Organization Details */}
            <Col xs={24} lg={16}>
              <Card
                title={
                  <Space style={{ fontSize: '20px', fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                    <BankOutlined style={{ color: brandColor, fontSize: '20px' }} />
                    Incubator Details
                  </Space>
                }
                bordered={false}
                style={{ height: '100%' }}
              >
                <Descriptions
                  column={{ xs: 1, sm: 2 }}
                  layout="vertical"
                  labelStyle={{
                    fontWeight: 400,
                    color: '#666',
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    textAlign: 'center'
                  }}
                  contentStyle={{
                    fontSize: '17px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}
                >
                  <Descriptions.Item label="Incubator Name">
                    <Text strong style={{ fontSize: 18, fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                      {incubatorData.incubatorName || 'N/A'}
                    </Text>
                  </Descriptions.Item>

                  <Descriptions.Item label="Director / CEO Name">
                    <Space>
                      <Text strong style={{ fontSize: 17, fontFamily: "'Rubik', sans-serif", fontWeight: 400 }}>
                        {incubatorData.directorName || incubatorData.founderName || 'N/A'}
                      </Text>
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Focus Areas">
                    {incubatorData.focusAreas || incubatorData.industry || 'Not Specified'}
                  </Descriptions.Item>

                  {incubatorData.establishedYear && (
                    <Descriptions.Item label="Established Year">
                      {incubatorData.establishedYear}
                    </Descriptions.Item>
                  )}

                  {incubatorData.programDuration && (
                    <Descriptions.Item label="Program Duration">
                      {incubatorData.programDuration}
                    </Descriptions.Item>
                  )}

                  {incubatorData.fundingAmount && (
                    <Descriptions.Item label="Funding Amount">
                      ₹ {incubatorData.fundingAmount}
                    </Descriptions.Item>
                  )}

                  {incubatorData.startupsSupported && (
                    <Descriptions.Item label="Startups Supported">
                      {incubatorData.startupsSupported}
                    </Descriptions.Item>
                  )}

                  <Descriptions.Item label="Status">
                    <Tag color={incubatorData.isActive ? 'success' : 'default'}>
                      {incubatorData.isActive ? 'Active' : 'Inactive'}
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                <Title
                  level={5}
                  style={{
                    marginBottom: 20,
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    fontSize: '20px',
                    color: brandColor
                  }}
                >
                  About
                </Title>
                <Text
                  type="secondary"
                  style={{
                    lineHeight: 1.8,
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: '#333'
                  }}
                >
                  {incubatorData.description || incubatorData.about ||
                    `${incubatorData.incubatorName || 'This incubator'} is a leading innovation hub supporting startups in ${incubatorData.focusAreas || incubatorData.industry || 'various'} sectors. 
                    We are committed to nurturing entrepreneurship and fostering innovation.`}
                </Text>
              </Card>
            </Col>

            {/* Right Column: Contact Information */}
            <Col xs={24} lg={8}>
              <Card
                title={
                  <Space style={{
                    fontSize: '20px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}>
                    <EnvironmentOutlined style={{ color: brandColor }} />
                    Contact Information
                  </Space>
                }
                bordered={false}
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                headStyle={{
                  borderBottom: 'none',
                  padding: '12px 12px 0 12px'
                }}
                bodyStyle={{
                  padding: '24px'
                }}
              >
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  <Divider style={{ margin: '3px 0' }} />
                  
                  {/* Contact Person */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <UserOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>
                        Contact Person
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                        {incubatorData.contactPerson || incubatorData.directorName || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MailOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666', textAlign: 'left' }}>
                        Email Address
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500, textAlign: 'left' }}>
                        {incubatorData.email || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Phone */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PhoneOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>
                        Mobile Number
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                        {incubatorData.phone || incubatorData.contactNumber || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  <Divider style={{ margin: '0px 0' }} />

                  {/* Location - Centered Layout */}
                  <div style={{ textAlign: 'center', marginTop: 0 }}>
                    <Text type="secondary" style={{ fontSize: 18, marginBottom: 8, display: 'block' }}>
                      Location
                    </Text>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'flex-start' }}>
                      <EnvironmentOutlined style={{ color: brandColor, marginTop: 4 }} />
                      <Text style={{
                        fontSize: 14,
                        color: '#333',
                        maxWidth: '80%',
                        lineHeight: 1.5
                      }}>
                        {incubatorData.location || incubatorData.city || incubatorData.address || 'Location not specified'}
                      </Text>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default IncubatorDetails;
